import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Get the API base URL - use relative URLs in production for custom domain support
export function getApiUrl() {
  // In development or when REACT_APP_BACKEND_URL is set and matches current origin, use it
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  
  // If we're on the same domain as the configured backend, use relative URLs
  // This ensures custom domains work correctly
  if (typeof window !== 'undefined') {
    const currentOrigin = window.location.origin;
    // Use relative URL (empty string) so requests go to current domain
    // The /api prefix will route to backend via ingress
    return '';
  }
  
  return envUrl || '';
}

// Export a constant for backward compatibility
export const API_URL = getApiUrl();

// Persistent token storage - uses multiple storage methods for iOS PWA reliability
const TOKEN_KEY = 'session_token';
const DB_NAME = 'weakenders_auth';
const STORE_NAME = 'tokens';

// IndexedDB helpers for more persistent storage on iOS
const openDB = () => {
  return new Promise((resolve, reject) => {
    if (!window.indexedDB) {
      reject(new Error('IndexedDB not supported'));
      return;
    }
    const request = indexedDB.open(DB_NAME, 1);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
  });
};

// Save token to multiple storage locations for reliability
export const saveToken = async (token) => {
  // Always save to localStorage
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch (e) {
    console.warn('localStorage save failed:', e);
  }
  
  // Also save to IndexedDB for iOS PWA persistence
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    store.put(token, TOKEN_KEY);
    await new Promise((resolve, reject) => {
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
    });
  } catch (e) {
    console.warn('IndexedDB save failed:', e);
  }
};

// Get token from any available storage
export const getToken = async () => {
  // Try localStorage first (fastest)
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) return token;
  } catch (e) {
    console.warn('localStorage read failed:', e);
  }
  
  // Fall back to IndexedDB
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const request = store.get(TOKEN_KEY);
    const token = await new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    if (token) {
      // Restore to localStorage if found in IndexedDB
      try {
        localStorage.setItem(TOKEN_KEY, token);
      } catch (e) {}
      return token;
    }
  } catch (e) {
    console.warn('IndexedDB read failed:', e);
  }
  
  return null;
};

// Clear token from all storage
export const clearToken = async () => {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch (e) {}
  
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    store.delete(TOKEN_KEY);
  } catch (e) {}
};

// Synchronous version for components that can't use async
export const getTokenSync = () => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch (e) {
    return null;
  }
};
